import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface CountUpProps {
  /** Target value (canonical prop). */
  end?: number;
  /** Alias of `end` for legacy call sites. */
  to?: number;
  /** Animation duration in ms (canonical prop). */
  durationMs?: number;
  /** Duration in seconds (legacy alias). Converted to ms internally. */
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}

export function CountUp({
  end,
  to,
  durationMs,
  duration,
  prefix = '',
  suffix = '',
  decimals = 0,
}: CountUpProps) {
  const targetValue = end ?? to ?? 0;
  const totalDuration = durationMs ?? (duration !== undefined ? duration * 1000 : 1600);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const reducedMotion = useReducedMotion();
  // Valeur finale par défaut : le SSR/prerender et les crawlers voient le bon
  // chiffre (pas "0"), et un scroll rapide n'affiche jamais de valeur vide.
  // L'animation rejoue depuis 0 uniquement quand l'élément entre dans le viewport.
  const [value, setValue] = useState(targetValue);

  useEffect(() => {
    if (!inView) return;
    if (reducedMotion) {
      setValue(targetValue);
      return;
    }
    let start: number | null = null;
    let raf: number;
    const tick = (ts: number) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / totalDuration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(eased * targetValue);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setValue(targetValue);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, targetValue, totalDuration, reducedMotion]);

  const formatted = value.toLocaleString('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
